package org.gstechnovos.jexonplastech.sections.home.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.gstechnovos.jexonplastech.sections.home.styles.QualityCardStyle
import org.gstechnovos.jexonplastech.sections.home.styles.QualityStandardsContainerStyle
import org.gstechnovos.jexonplastech.theme.toSitePalette
import org.gstechnovos.jexonplastech.theme.unextractable
import org.gstechnovos.jexonplastech.util.LocalBreakpoint
import org.gstechnovos.jexonplastech.util.Res
import org.gstechnovos.jexonplastech.util.animateSlideUp
import org.gstechnovos.jexonplastech.util.rememberScrollVisibility
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

// ==========================================
// CMS DATA MODEL
// ==========================================

data class QualityStandardItem(
    val title: String, val description: String, val iconPath: String
)

/**
 *
 * Images (Icons): Dimensions/Aspect Ratio: Minimum 128x128px; strictly 1:1 (Square).
 * SVG format is highly preferred for crisp scaling.
 * Rendering Behavior: Icons are hard-coded to display exactly at 64x64px.
 *
 * Text:Overline: Renders at 14px in uppercase. Limit to 2-3 words.
 * Headline: Renders at 36px on desktop and 28px on mobile. Limit to 3-6 words.
 *
 * Card Titles: Renders at 20px. Limit to 3-5 words to avoid excessive line breaks.
 * Card Descriptions: Renders at 16px. Limit to 2-3 sentences.
 * Tip: Keep the text length across all three cards as identical as possible so
 * the bottom of the grid remains perfectly aligned.
 *
 */
data class HomeQualityStandardsData(
    val overline: String, val headline: String, val items: List<QualityStandardItem>
)

val defaultQualityStandardsData = HomeQualityStandardsData(
    overline = "OUR COMMITMENT", headline = "Manufacturing Quality & Standards", items = listOf(
        QualityStandardItem(
            title = "100% BPA-Free Materials",
            description = "Our food storage and homeware products are manufactured using non-toxic, food-grade plastics, ensuring absolute safety and compliance.",
            iconPath = Res.Image.Home.Manufacturing.BPA_FREE
        ), QualityStandardItem(
            title = "Engineered for Durability",
            description = "From cleaning brushes to floor wipers, our goods undergo rigorous stress testing to guarantee long-lasting utility in demanding environments.",
            iconPath = Res.Image.Home.Manufacturing.DURABILITY
        ), QualityStandardItem(
            title = "High-Volume Capacity",
            description = "Equipped with state-of-the-art machinery, our facility easily scales production to meet massive B2B and vendor distribution demands globally.",
            iconPath = Res.Image.Home.Manufacturing.PRODUCTION
        )
    )
)

// ==========================================
// COMPONENT
// ==========================================

@Composable
fun HomeQualityStandardsSection(data: HomeQualityStandardsData = defaultQualityStandardsData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()

    val (isVisible, visibilityRef) = rememberScrollVisibility(threshold = 0.2)

    Column(
        modifier = QualityStandardsContainerStyle.toModifier().padding(
            topBottom = if (isDesktop) 80.px else 40.px, leftRight = 5.percent
        ), horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Div(attrs = Modifier.fillMaxWidth().toAttrs {
            ref { element ->
                visibilityRef(element)
                onDispose {}
            }
        }) {
            // Keep your content centered inside the Div
            Column(
                modifier = Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // --- SECTION HEADER ---
                SpanText(
                    text = data.overline,
                    modifier = Modifier.animateSlideUp(isVisible, delayMs = 0).color(palette.brand.primaryBlue)
                        .fontSize(14.px).fontWeight(FontWeight.Bold).letterSpacing(1.5.px)
                        .textTransform(TextTransform.Uppercase).margin(bottom = 12.px)
                )

                SpanText(
                    text = data.headline,
                    modifier = Modifier.animateSlideUp(isVisible, delayMs = 150).color(palette.custom.darkText)
                        .fontSize(if (isDesktop) 36.px else 28.px).fontWeight(FontWeight.Bold).margin(bottom = 48.px)
                        .textAlign(TextAlign.Center)
                )

                // --- STANDARDS GRID ---
                SimpleGrid(
                    // 1 column on mobile, 3 columns on tablet and desktop
                    numColumns(1, md = 3), modifier = Modifier.fillMaxWidth().maxWidth(1200.px).gap(30.px)
                ) {
                    data.items.forEachIndexed { index, item ->
                        // Wrap the card inside an animation Box
                        Box(modifier = Modifier.animateSlideUp(isVisible, delayMs = 300 + (index * 200))) {
                            QualityCard(item)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun QualityCard(item: QualityStandardItem) {
    val palette = ColorMode.current.toSitePalette()

    Column(
        modifier = QualityCardStyle.toModifier(), horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // Standard Icon
        Image(
            src = item.iconPath,
            alt = item.title,
            modifier = Modifier.size(64.px).margin(bottom = 24.px).unextractable()
        )

        // Title
        SpanText(
            text = item.title,
            modifier = Modifier.color(palette.custom.darkText).fontSize(20.px).fontWeight(FontWeight.Bold)
                .margin(bottom = 16.px).textAlign(TextAlign.Center)
        )

        // Description
        SpanText(
            text = item.description,
            modifier = Modifier.color(palette.custom.darkText.toRgb().copyf(alpha = 0.7f)).fontSize(16.px)
                .lineHeight(1.6).textAlign(TextAlign.Center)
        )
    }
}